/**
 * PageBuilderManagement — admin panel for editing the "join-us-as-library"
 * marketing page via the editable-pages API.
 *
 * Renders a section list (SectionRow per section) plus a live preview toggle,
 * status indicator, and Save / Publish / Unpublish / Reset actions.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import {
  Box, Typography, Button, Chip, CircularProgress, Snackbar,
  Alert, Dialog, DialogTitle, DialogContent, DialogActions,
  Divider, Tooltip, useTheme, Tabs, Tab, Paper,
  List, ListItem, ListItemButton, ListItemText,
} from '@mui/material';
import {
  Save, Publish, UnpublishOutlined, RestartAlt, OpenInNew,
  CheckCircle, Edit, Visibility, Add,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { makePageBuilderApi } from './pageBuilder/pageBuilderApi';
import SectionRow from './pageBuilder/SectionRow';
import { DEFAULT_LAYOUTS } from '../../marketing/editable/defaultLayout';
import sectionRegistry from '../../marketing/editable/sectionRegistry';
import sectionFieldSchemas from './pageBuilder/sectionFieldSchemas';
import {
  SectionHeader, GlassPaper,
} from './ui';
import { glass, ADMIN_GRADIENTS, ADMIN_COLORS } from './ui/glassTokens';

// ─── helpers ──────────────────────────────────────────────────────────────────
function uid() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}

function immutableSwap(arr, i, j) {
  const next = [...arr];
  [next[i], next[j]] = [next[j], next[i]];
  return next;
}

function immutableUpdate(arr, idx, updater) {
  return arr.map((item, i) => (i === idx ? updater(item) : item));
}

function immutableRemove(arr, idx) {
  return arr.filter((_, i) => i !== idx);
}

// ─── Generic section defaults (no content prefill) ────────────────────────────
const GENERIC_DEFAULTS = {
  genericHero: {
    eyebrow: '',
    heading: '',
    subheading: '',
    body: '',
    alignment: 'left',
    backgroundColor: '',
    backgroundImage: '',
    buttons: [],
  },
  genericContent: {
    heading: '',
    body: '',
    alignment: 'left',
  },
  genericMedia: {
    imageUrl: '',
    caption: '',
    alignment: 'center',
    width: 'full',
  },
  genericItems: {
    eyebrow: '',
    heading: '',
    subheading: '',
    alignment: 'center',
    items: [],
  },
  genericCta: {
    heading: '',
    text: '',
    alignment: 'center',
    buttons: [],
  },
  libraries: {
    eyebrow: '',
    title: '',
    subtitle: '',
    library_ids: [],
  },
  spacer: {
    size: 'medium',
    divider: false,
  },
};

/**
 * Returns the default props for a section type.
 *
 * Generic types: return a blank shell from GENERIC_DEFAULTS (no content prefill).
 * Legacy types: look up DEFAULT_LAYOUTS[slug] for the prefilled defaults.
 * Unknown types: return {}.
 */
function defaultPropsForType(type, slug) {
  if (type in GENERIC_DEFAULTS) {
    return { ...GENERIC_DEFAULTS[type] };
  }
  const defaultLayout = DEFAULT_LAYOUTS[slug] || [];
  const found = defaultLayout.find(s => s.type === type);
  return found ? { ...(found.props || {}) } : {};
}

/**
 * Section types that can be added to a generic (non-join-us-as-library) page.
 * Only the 7 content-agnostic generic types are offered.
 */
const GENERIC_SECTION_TYPES = [
  'genericHero',
  'genericContent',
  'genericMedia',
  'genericItems',
  'genericCta',
  'libraries',
  'spacer',
];

/**
 * Legacy section types: all types that exist in both schemas and registry,
 * used only for the join-us-as-library slug.
 */
const LEGACY_ADDABLE_SECTION_TYPES = Object.keys(sectionFieldSchemas).filter(
  type => type in sectionRegistry && !GENERIC_SECTION_TYPES.includes(type)
);

// ─── LivePreview — renders the in-state layout using sectionRegistry ──────────
function LivePreview({ layout }) {
  const theme = useTheme();

  // Filter visible sections and render each via the registry
  const visible = layout.filter(s => s.visible);

  if (visible.length === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 300,
          color: 'text.secondary',
        }}
      >
        <Typography variant="body2">All sections are hidden — nothing to preview.</Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        // Scaled scrollable container so the preview fits the admin panel
        width: '100%',
        overflowY: 'auto',
        overflowX: 'hidden',
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 2,
        bgcolor: '#ffffff', // preview always on white (it's a public marketing page)
        color: '#0F172A',
        // Reset any admin-panel dark theme that would bleed into the preview iframe
        '& *': { boxSizing: 'border-box' },
      }}
    >
      {/* Inner wrapper — we render at 60% scale so it fits in the panel */}
      <Box
        sx={{
          transformOrigin: 'top left',
          transform: 'scale(0.6)',
          width: '166.67%', // 1/0.6 to cancel the scale width shrink
          minHeight: 400,
        }}
      >
        {visible.map(section => {
          const Component = sectionRegistry[section.type];
          if (!Component) return null;

          // Dynamic sections receive empty/minimal dynamic props for preview.
          // They default gracefully per section implementation.
          const dynamicProps =
            section.type === 'featuredLibraries' || section.type === 'libraries'
              ? { libraries: [], loading: false }
              : section.type === 'socialProof'
              ? { totalCount: 0, libraries: [] }
              : {};

          return (
            <React.Fragment key={section.id}>
              <Component {...section.props} {...dynamicProps} />
            </React.Fragment>
          );
        })}
      </Box>
    </Box>
  );
}

// ─── StatusChip ──────────────────────────────────────────────────────────────
function StatusChip({ status, publishedAt }) {
  const isPublished = status === 'published';
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
      <Chip
        size="small"
        icon={isPublished ? <CheckCircle sx={{ fontSize: '14px !important' }} /> : <Edit sx={{ fontSize: '14px !important' }} />}
        label={isPublished ? 'Published' : 'Draft'}
        color={isPublished ? 'success' : 'default'}
        sx={{ fontWeight: 600 }}
      />
      {isPublished && publishedAt && (
        <Typography variant="caption" color="text.secondary">
          Last published {format(new Date(publishedAt), 'd MMM yyyy, h:mm a')}
        </Typography>
      )}
    </Box>
  );
}

// ─── ConfirmDialog ─────────────────────────────────────────────────────────────
function ConfirmDialog({ open, title, body, confirmLabel, confirmColor = 'primary', onConfirm, onClose, loading }) {
  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>{title}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary">{body}</Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={loading}>Cancel</Button>
        <Button
          variant="contained"
          color={confirmColor}
          onClick={onConfirm}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {loading ? 'Working…' : confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── PageBuilderManagement (default export) ────────────────────────────────────

/**
 * PageBuilderManagement
 *
 * @param {string} apiBase - Base path for editable-pages API calls.
 *   Default: '/admin/editable-pages'. Pass '/admin/marketing-pages' to
 *   use the admin-managed marketing pages API.
 * @param {string} slug - The page slug to load/edit.
 *   Default: 'join-us-as-library' for back-compat.
 */
export default function PageBuilderManagement({ apiBase = '/admin/editable-pages', slug = 'join-us-as-library' }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const g = glass(theme);

  // ── Page state ──────────────────────────────────────────────────────────────
  const [layout, setLayout] = useState([]);
  const [title, setTitle] = useState('');
  const [status, setStatus] = useState('draft');
  const [publishedAt, setPublishedAt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  // ── Action state ────────────────────────────────────────────────────────────
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [unpublishing, setUnpublishing] = useState(false);

  // ── UI state ────────────────────────────────────────────────────────────────
  const [expandedId, setExpandedId] = useState(null); // only one expanded at a time
  const [activeTab, setActiveTab] = useState(0);      // 0 = editor, 1 = preview
  const [publishConfirmOpen, setPublishConfirmOpen] = useState(false);
  const [unpublishConfirmOpen, setUnpublishConfirmOpen] = useState(false);
  const [resetConfirmOpen, setResetConfirmOpen] = useState(false);
  const [addSectionOpen, setAddSectionOpen] = useState(false);
  const [removeSectionIdx, setRemoveSectionIdx] = useState(null); // null = closed
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // ── Fetch on mount ───────────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    const _pbApi = makePageBuilderApi(apiBase);
    (async () => {
      try {
        setLoading(true);
        setLoadError(null);
        const data = await _pbApi.getPage(slug);
        if (cancelled) return;
        setLayout(data.layout || DEFAULT_LAYOUTS[slug]);
        setTitle(data.title || '');
        setStatus(data.status || 'draft');
        setPublishedAt(data.published_at || null);
      } catch (err) {
        if (!cancelled) {
          setLoadError(err.message || 'Failed to load page');
          // Fall back to defaults so the editor is still usable
          setLayout(DEFAULT_LAYOUTS[slug]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [apiBase, slug]);

  // ── Snackbar helpers ─────────────────────────────────────────────────────────
  const showSnackbar = useCallback((message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  }, []);

  const closeSnackbar = useCallback(() => {
    setSnackbar(s => ({ ...s, open: false }));
  }, []);

  // ── Section mutation handlers ────────────────────────────────────────────────
  const handleVisibilityChange = useCallback((idx, visible) => {
    setLayout(prev => immutableUpdate(prev, idx, s => ({ ...s, visible })));
  }, []);

  const handleMoveUp = useCallback((idx) => {
    if (idx === 0) return;
    setLayout(prev => immutableSwap(prev, idx, idx - 1));
  }, []);

  const handleMoveDown = useCallback((idx) => {
    setLayout(prev => {
      if (idx >= prev.length - 1) return prev;
      return immutableSwap(prev, idx, idx + 1);
    });
  }, []);

  const handlePropsChange = useCallback((idx, newProps) => {
    setLayout(prev => immutableUpdate(prev, idx, s => ({ ...s, props: newProps })));
  }, []);

  const handleToggleExpand = useCallback((id) => {
    setExpandedId(prev => (prev === id ? null : id));
  }, []);

  // ── Add section ──────────────────────────────────────────────────────────────

  // Which section types are available in the add-section menu?
  // join-us-as-library keeps the full legacy set; every other slug gets only generic types.
  const addableSectionTypes = slug === 'join-us-as-library'
    ? LEGACY_ADDABLE_SECTION_TYPES
    : GENERIC_SECTION_TYPES;

  const handleAddSection = useCallback((type) => {
    const newSection = {
      id: uid(),
      type,
      visible: true,
      props: defaultPropsForType(type, slug),
    };
    setLayout(prev => [...prev, newSection]);
    setAddSectionOpen(false);
    showSnackbar(`"${sectionFieldSchemas[type]?.label || type}" section added — scroll down to edit it`);
  }, [showSnackbar, slug]);

  // ── Remove section ───────────────────────────────────────────────────────────
  const handleConfirmRemoveSection = useCallback(() => {
    if (removeSectionIdx === null) return;
    setLayout(prev => immutableRemove(prev, removeSectionIdx));
    setExpandedId(prev => {
      // If the removed section was expanded, close the panel
      return prev; // SectionRow keys by id so stale id will just not match — safe
    });
    setRemoveSectionIdx(null);
    showSnackbar('Section removed — save draft to persist');
  }, [removeSectionIdx, showSnackbar]);

  // ── Save draft ───────────────────────────────────────────────────────────────
  const handleSaveDraft = useCallback(async () => {
    try {
      setSaving(true);
      const _pbApi = makePageBuilderApi(apiBase);
      const data = await _pbApi.saveDraft(slug, { title: title || undefined, layout });
      setLayout(data.layout);
      setStatus(data.status);
      showSnackbar('Draft saved successfully');
    } catch (err) {
      let msg = err.message || 'Failed to save draft';
      if (err.errors && err.errors.length > 0) {
        msg += ': ' + err.errors.map(e => e.msg || e.message || JSON.stringify(e)).join('; ');
      }
      showSnackbar(msg, 'error');
    } finally {
      setSaving(false);
    }
  }, [layout, title, showSnackbar, apiBase]);

  // ── Publish ───────────────────────────────────────────────────────────────────
  const handlePublish = useCallback(async () => {
    try {
      setPublishing(true);
      const _pbApi = makePageBuilderApi(apiBase);
      // Always save the current layout first so publish reflects latest edits
      const saved = await _pbApi.saveDraft(slug, { title: title || undefined, layout });
      setLayout(saved.layout);
      const data = await _pbApi.publish(slug);
      setStatus(data.status);
      setPublishedAt(data.published_at || new Date().toISOString());
      showSnackbar('Page published successfully');
    } catch (err) {
      let msg = err.message || 'Failed to publish';
      if (err.errors && err.errors.length > 0) {
        msg += ': ' + err.errors.map(e => e.msg || e.message || JSON.stringify(e)).join('; ');
      }
      showSnackbar(msg, 'error');
    } finally {
      setPublishing(false);
      setPublishConfirmOpen(false);
    }
  }, [layout, title, showSnackbar, apiBase]);

  // ── Unpublish ─────────────────────────────────────────────────────────────────
  const handleUnpublish = useCallback(async () => {
    try {
      setUnpublishing(true);
      const _pbApi = makePageBuilderApi(apiBase);
      const data = await _pbApi.unpublish(slug);
      setStatus(data.status);
      setPublishedAt(null);
      showSnackbar('Page unpublished — it now shows the default design');
    } catch (err) {
      showSnackbar(err.message || 'Failed to unpublish', 'error');
    } finally {
      setUnpublishing(false);
      setUnpublishConfirmOpen(false);
    }
  }, [showSnackbar, apiBase]);

  // ── Reset to defaults ─────────────────────────────────────────────────────────
  const handleReset = useCallback(() => {
    setLayout(DEFAULT_LAYOUTS[slug]);
    setExpandedId(null);
    setResetConfirmOpen(false);
    showSnackbar('Reset to defaults — save to persist this change');
  }, [showSnackbar]);

  // ─────────────────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  const anyActionLoading = saving || publishing || unpublishing;

  return (
    <Box sx={{ maxWidth: 960, mx: 'auto', pb: 8 }}>
      {/* ── Page header ── */}
      <SectionHeader
        eyebrow="Marketing"
        title={title ? `Editing: ${title}` : `Editing: ${slug}`}
        description="Edit the content and layout of this marketing page. Changes are saved as drafts and only go live when you publish."
        action={
          <Button
            size="small"
            variant="outlined"
            startIcon={<OpenInNew />}
            href={slug === 'join-us-as-library' ? '/join-us-as-library' : `/p/${slug}`}
            target="_blank"
            rel="noopener noreferrer"
            sx={{ borderRadius: 2 }}
          >
            View live page
          </Button>
        }
      />

      {/* ── Load error banner ── */}
      {loadError && (
        <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
          Could not load saved content ({loadError}). Showing default layout — you can still edit and save.
        </Alert>
      )}

      {/* ── Status + action bar ── */}
      <GlassPaper sx={{ p: 2, mb: 3, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <StatusChip status={status} publishedAt={publishedAt} />

        <Box sx={{ flex: 1 }} />

        <Tooltip title="Reset all fields to their original defaults (local only — save to persist)">
          <Button
            size="small"
            variant="outlined"
            color="warning"
            startIcon={<RestartAlt />}
            onClick={() => setResetConfirmOpen(true)}
            disabled={anyActionLoading}
            sx={{ borderRadius: 2 }}
          >
            Reset
          </Button>
        </Tooltip>

        <Button
          size="small"
          variant="outlined"
          startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <Save />}
          onClick={handleSaveDraft}
          disabled={anyActionLoading}
          sx={{ borderRadius: 2 }}
        >
          {saving ? 'Saving…' : 'Save draft'}
        </Button>

        {status === 'published' && (
          <Button
            size="small"
            variant="outlined"
            color="error"
            onClick={() => setUnpublishConfirmOpen(true)}
            disabled={anyActionLoading}
            sx={{ borderRadius: 2 }}
          >
            Unpublish
          </Button>
        )}

        <Button
          size="small"
          variant="contained"
          startIcon={publishing ? <CircularProgress size={14} color="inherit" /> : <Publish />}
          onClick={() => setPublishConfirmOpen(true)}
          disabled={anyActionLoading}
          sx={{
            borderRadius: 2,
            background: ADMIN_GRADIENTS.accent,
            '&:hover': { background: ADMIN_GRADIENTS.accentShort },
          }}
        >
          {publishing ? 'Publishing…' : 'Publish'}
        </Button>
      </GlassPaper>

      {/* ── Editor / Preview tabs ── */}
      <Box sx={{ mb: 2 }}>
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          sx={{ borderBottom: `1px solid ${theme.palette.divider}` }}
        >
          <Tab
            label="Editor"
            icon={<Edit sx={{ fontSize: 16 }} />}
            iconPosition="start"
            sx={{ minHeight: 44, textTransform: 'none', fontWeight: 600 }}
          />
          <Tab
            label="Live preview"
            icon={<Visibility sx={{ fontSize: 16 }} />}
            iconPosition="start"
            sx={{ minHeight: 44, textTransform: 'none', fontWeight: 600 }}
          />
        </Tabs>
      </Box>

      {/* ── Editor tab ── */}
      {activeTab === 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {/* Section list header */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 0.5 }}>
            <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
              {layout.length} sections · use arrows to reorder · toggle switch to show/hide
            </Typography>
            <Tooltip title="Append a new section to the page">
              <Button
                size="small"
                variant="outlined"
                startIcon={<Add />}
                onClick={() => setAddSectionOpen(true)}
                disabled={anyActionLoading}
                sx={{ borderRadius: 2, whiteSpace: 'nowrap' }}
              >
                Add section
              </Button>
            </Tooltip>
          </Box>

          {layout.map((section, idx) => (
            <SectionRow
              key={section.id}
              section={section}
              index={idx}
              total={layout.length}
              isExpanded={expandedId === section.id}
              onToggleExpand={() => handleToggleExpand(section.id)}
              onVisibilityChange={visible => handleVisibilityChange(idx, visible)}
              onMoveUp={() => handleMoveUp(idx)}
              onMoveDown={() => handleMoveDown(idx)}
              onPropsChange={newProps => handlePropsChange(idx, newProps)}
              onRemove={anyActionLoading ? undefined : () => setRemoveSectionIdx(idx)}
            />
          ))}
        </Box>
      )}

      {/* ── Preview tab ── */}
      {activeTab === 1 && (
        <Box>
          <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }} icon={<Visibility />}>
            Live preview reflects your current unsaved edits. Dynamic data (library counts, featured libraries) will appear as empty/zero — they load from the real API on the public page.
          </Alert>
          <LivePreview layout={layout} />
        </Box>
      )}

      {/* ── Confirm dialogs ── */}
      <ConfirmDialog
        open={publishConfirmOpen}
        title="Publish page?"
        body="Your current draft will be saved and made live. Visitors will immediately see the updated content."
        confirmLabel="Publish"
        confirmColor="primary"
        loading={publishing}
        onConfirm={handlePublish}
        onClose={() => setPublishConfirmOpen(false)}
      />

      <ConfirmDialog
        open={unpublishConfirmOpen}
        title="Unpublish page?"
        body="The live page will revert to the default hard-coded design. Your draft is kept and can be republished at any time."
        confirmLabel="Unpublish"
        confirmColor="error"
        loading={unpublishing}
        onConfirm={handleUnpublish}
        onClose={() => setUnpublishConfirmOpen(false)}
      />

      <ConfirmDialog
        open={resetConfirmOpen}
        title="Reset to defaults?"
        body="All section content will be replaced with the original default values. This only changes your local editor state — save the draft to persist it."
        confirmLabel="Reset"
        confirmColor="warning"
        loading={false}
        onConfirm={handleReset}
        onClose={() => setResetConfirmOpen(false)}
      />

      {/* ── Add Section dialog ── */}
      <Dialog
        open={addSectionOpen}
        onClose={() => setAddSectionOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Add a section</DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <Typography variant="body2" color="text.secondary" sx={{ px: 3, pb: 1 }}>
            The new section is appended to the bottom of the page. You can reorder it using the arrow
            buttons in the editor.
          </Typography>
          <List disablePadding>
            {addableSectionTypes.map((type) => (
              <ListItem key={type} disablePadding>
                <ListItemButton
                  onClick={() => handleAddSection(type)}
                  sx={{ px: 3, py: 1.25 }}
                >
                  <ListItemText
                    primary={sectionFieldSchemas[type]?.label || type}
                    primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setAddSectionOpen(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>

      {/* ── Remove Section confirm dialog ── */}
      <ConfirmDialog
        open={removeSectionIdx !== null}
        title="Remove section?"
        body={
          removeSectionIdx !== null && layout[removeSectionIdx]
            ? `Remove "${sectionFieldSchemas[layout[removeSectionIdx].type]?.label || layout[removeSectionIdx].type}"? This only affects your local editor state — save the draft to persist the change.`
            : 'Remove this section from the page?'
        }
        confirmLabel="Remove"
        confirmColor="error"
        loading={false}
        onConfirm={handleConfirmRemoveSection}
        onClose={() => setRemoveSectionIdx(null)}
      />

      {/* ── Snackbar ── */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={snackbar.severity === 'error' ? null : 4000}
        onClose={closeSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={closeSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%', maxWidth: 480, borderRadius: 2 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

PageBuilderManagement.propTypes = {
  apiBase: PropTypes.string,
  slug: PropTypes.string,
};
